import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Label from "@/components/Label";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { TEAM, BUSINESS } from "@/data/content";

export default function Team() {
  const founder = TEAM.find((m) => m.founder);
  const rest = TEAM.filter((m) => !m.founder);

  return (
    <div data-testid="team-page">
      <Seo
        title="Meet the Team"
        path="/team"
        description="Meet the experienced estheticians behind Barrhaven Beauty Lounge — a passionate team delivering expert facials, laser and skincare treatments in Nepean, Ottawa."
      />
      <PageHero
        script="Our Team"
        title="The hands behind the glow."
        subtitle="Experienced, passionate and detail-oriented — our estheticians provide personalised care in a warm, welcoming environment."
      />

      {/* Founder feature — dark section for contrast */}
      {founder && (
        <section className="relative bg-[#050505] text-white grain overflow-hidden">
          <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 md:px-10 py-16 md:py-24 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <div
                  data-testid={`team-member-${founder.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-white/5"
                >
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </Reveal>
            </div>
            <div className="flex flex-col justify-center lg:col-span-7">
              <Reveal delay={0.1}>
                <Label dark className="mb-5">Meet The Founder</Label>
                <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight">
                  {founder.name}
                </h2>
                <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white/50">
                  {founder.role}
                </p>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-white/70">
                  {founder.bio}
                </p>
                <a
                  href={BUSINESS.bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group mt-8 inline-flex items-center gap-2 border border-white px-8 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505]"
                >
                  Book with our team
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Team grid — colourful photos */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <Label className="mb-4">Our Estheticians</Label>
          <h2 className="mb-12 font-serif-display text-4xl md:text-5xl tracking-tight">
            The team you&apos;ll meet
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((m, i) => (
            <Reveal key={m.name} delay={(i % 3) * 0.08}>
              <article
                data-testid={`team-member-${m.name.toLowerCase()}`}
                className="flex h-full flex-col"
              >
                <div className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#f5f5f4]">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-6 font-serif-display text-2xl md:text-3xl tracking-tight">
                  {m.name}
                </h3>
                <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[#888]">
                  {m.role}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#3d3d3d]">{m.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-[#f5f5f4]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-serif-display text-4xl md:text-5xl font-normal tracking-tight">
              Come meet us in person.
            </h2>
            <a
              href={BUSINESS.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="group mt-8 inline-flex items-center gap-2 bg-[#050505] px-10 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505] border border-[#050505]"
            >
              Book an appointment
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
